import { useState, useEffect } from 'react';
import { Sparkles, MessageSquare, Wand2, Workflow, FileText, Users, ShieldCheck, TrendingUp, DollarSign, UserSearch } from 'lucide-react';
import { AIMessageGenerator } from '../../components/AIMessageGenerator';
import { AIMessageImprover } from '../../components/AIMessageImprover';
import { AIBriefGenerator } from '../../components/AIBriefGenerator';
import { AIKOLSuggestions } from '../../components/AIKOLSuggestions';
import { AIInsightsPanel } from '../../components/AIInsightsPanel';
import { AIWorkflowPanel } from '../../components/AIWorkflowPanel';
import { AIPricingPanel } from '../../components/AIPricingPanel';
import { AIKOLDiscoveryPanel } from '../../components/AIKOLDiscoveryPanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import api from '../../lib/api';

const SCENARIOS = [
  {
    id: 'invitation',
    title: 'Invitation Message',
    description: 'Generate a personalized KOL campaign invitation',
    endpoint: '/ai-messages/generate',
    payload: {
      scenario: 'invitation_broadcast',
      jobTitle: 'Summer Fashion Campaign 2025',
      kolName: 'Sarah Chen',
      agencyName: 'Creative Minds Agency',
      jobDescription: 'Instagram Reels showcasing summer collection with authentic lifestyle content.',
    },
  },
  {
    id: 'followup',
    title: 'Follow-up Message',
    description: 'Send a polite reminder for pending invitations',
    endpoint: '/ai-messages/generate',
    payload: {
      scenario: 'invitation_follow_up',
      jobTitle: 'Summer Fashion Campaign 2025',
      kolName: 'Sarah Chen',
      agencyName: 'Creative Minds Agency',
    },
  },
  {
    id: 'script-revision',
    title: 'Script Revision Feedback',
    description: 'Corporate/Agency feedback when a script needs changes',
    endpoint: '/ai-messages/generate',
    payload: {
      scenario: 'script_revision_request',
      jobTitle: 'Summer Fashion Campaign 2025',
      kolName: 'Sarah Chen',
      revisionReason: 'Tone is too casual and key brand talking points are missing',
    },
  },
  {
    id: 'submission-revision',
    title: 'Submission Revision Feedback',
    description: 'Detailed feedback when final content needs revision',
    endpoint: '/ai-messages/generate',
    payload: {
      scenario: 'submission_revision_request',
      jobTitle: 'Summer Fashion Campaign 2025',
      kolName: 'Sarah Chen',
      revisionReason: 'Logo placement is off-brand and caption needs clearer CTA',
    },
  },
  {
    id: 'approval',
    title: 'Approval Message',
    description: 'Congratulate KOL when content is approved',
    endpoint: '/ai-messages/generate',
    payload: {
      scenario: 'submission_approval',
      jobTitle: 'Summer Fashion Campaign 2025',
      kolName: 'Sarah Chen',
    },
  },
  {
    id: 'deadline',
    title: 'Deadline Reminder',
    description: 'Proactive reminder before submission deadline',
    endpoint: '/ai-messages/generate',
    payload: {
      scenario: 'deadline_reminder',
      jobTitle: 'Summer Fashion Campaign 2025',
      kolName: 'Sarah Chen',
      specificDetails: 'Final video submission due in 2 days',
    },
  },
];

const WORKFLOW_INTEGRATIONS = [
  {
    route: 'POST /ai/jobs/draft-suggestion',
    feature: 'Generate complete job brief from short description (Create Job)',
  },
  {
    route: 'POST /ai/broadcast/rerank',
    feature: 'AI-enhanced KOL matching with blended scores (Job Broadcast)',
  },
  {
    route: 'POST /ai/scripts/generate',
    feature: 'AI script generation for campaigns',
  },
  {
    route: 'POST /ai/content-qa/script',
    feature: 'Content QA — comprehensive script review against campaign brief',
  },
  {
    route: 'POST /submissions/:id/ai-check',
    feature: 'Content QA — pre-screen submission caption and metadata',
  },
  {
    route: 'POST /ai/workflow/job-scan',
    feature: 'Workflow Automation — stalled items, SLA alerts, task priorities per job',
  },
  {
    route: 'POST /ai/workflow/agency-scan',
    feature: 'Workflow Automation — agency-wide orchestration and smart reminders',
  },
  {
    route: 'POST /ai/analytics/job-insights',
    feature: 'Predictive Analytics — campaign forecasts and bottleneck detection',
  },
  {
    route: 'POST /ai/analytics/agency-insights',
    feature: 'Predictive Analytics — agency portfolio health and opportunities',
  },
  {
    route: 'POST /ai/budget/plan',
    feature: 'Dynamic Pricing — AI budget allocation across shortlisted KOLs',
  },
  {
    route: 'POST /ai/pricing/job-analysis',
    feature: 'Dynamic Pricing — market benchmarks, budget health, and scenarios',
  },
  {
    route: 'POST /ai/pricing/kol-rate',
    feature: 'Dynamic Pricing — per-KOL rate negotiation suggestions',
  },
  {
    route: 'POST /ai/discovery/job-scan',
    feature: 'KOL Discovery — talent gaps and recruitment candidates per job',
  },
  {
    route: 'POST /ai/discovery/agency-scan',
    feature: 'KOL Discovery — agency portfolio health and niche trends',
  },
  {
    route: 'POST /ai/discovery/vet',
    feature: 'KOL Discovery — authenticity, brand safety, and content vetting',
  },
  {
    route: 'POST /ai/discovery/outreach-draft',
    feature: 'KOL Discovery — personalized recruitment outreach drafts',
  },
  {
    route: 'POST /ai-messages/*',
    feature: 'Communication Assistant — messages, sentiment, tone improvement',
  },
  {
    route: 'POST /invitations/:id/messages',
    feature: 'Send AI-generated invitation messages with useAI flag',
  },
  {
    route: 'POST /invitations/:id/messages/ai-draft',
    feature: 'Preview AI message before sending to KOL',
  },
  {
    route: 'POST /scripts/:id/ai-revision-feedback',
    feature: 'Preview AI revision feedback for scripts',
  },
  {
    route: 'POST /submissions/:id/request-revision',
    feature: 'AI-enhanced submission revision feedback',
  },
  {
    route: 'POST /submissions/:id/ai-revision-feedback',
    feature: 'Preview AI revision feedback for submissions',
  },
];

export default function AIDemo() {
  const [activeScenario, setActiveScenario] = useState(SCENARIOS[0].id);
  const [acceptedMessage, setAcceptedMessage] = useState<string | null>(null);
  const [improvedMessage, setImprovedMessage] = useState<string | null>(null);
  const [demoJobId, setDemoJobId] = useState<string | null>(null);

  useEffect(() => {
    api.get('/jobs?status=DRAFT').then((res) => {
      const draft = res.data.find((j: any) => j.title?.includes('Basketball')) || res.data[0];
      if (draft) setDemoJobId(draft.id);
    }).catch(() => {});
  }, []);

  const scenario = SCENARIOS.find((s) => s.id === activeScenario)!;

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="h-8 w-8 text-accent2" />
          <h1 className="page-title">AI Agents Demo</h1>
          <Badge variant="success">8 Agents Implemented</Badge>
        </div>
        <p className="text-glass-text-2 max-w-3xl text-sm">
          Explore all AI features on the merged dev branch: DeepSeek-powered briefing, matching, scripts,
          predictive analytics, workflow automation, dynamic pricing, KOL discovery, plus the Communication
          Assistant (OpenAI or demo mode).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-5 w-5" />
            Smart Briefing Assistant
          </CardTitle>
          <CardDescription>
            Generate a complete job brief from a short campaign description — integrated in Create Job page
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AIBriefGenerator
            onApply={(brief) => {
              alert(`Brief generated: "${brief.title}" — visit Jobs → Create New Job to apply in the full form.`);
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Users className="h-5 w-5" />
            Intelligent KOL Matching
          </CardTitle>
          <CardDescription>
            AI-enhanced recommendations with reasoning and risk flags — integrated in Job Detail broadcast flow
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-glass-text-2 mb-4">
            To try this feature, open a DRAFT job (e.g. &quot;Nike Basketball x NBA All-Star Weekend&quot;),
            go to the Broadcast tab, find matching KOLs, and click &quot;Get AI Suggestions&quot;.
          </p>
          {demoJobId ? (
            <AIKOLSuggestions jobId={demoJobId} />
          ) : (
            <p className="text-sm text-glass-text-3">Loading demo job...</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <ShieldCheck className="h-5 w-5" />
            Content Quality Assurance
          </CardTitle>
          <CardDescription>
            Pre-screen scripts and submissions for brand alignment, compliance, and brief fit — integrated in Script Workspace and Submission Detail
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-glass-text-2">
            Open <strong className="text-glass-text">Agency → Scripts</strong> for script QA, or <strong className="text-glass-text">Agency → Submissions</strong> for submission pre-screening.
            Works in demo mode when DeepSeek is not configured.
          </p>
          <div className="rounded-xl border border-dashed border-glass bg-glass-2 p-4 text-sm text-glass-text-2 space-y-2">
            <p>
              <code className="text-xs bg-glass-3 text-glass-text px-1 rounded">POST /api/ai/content-qa/script</code> — script review against brief
            </p>
            <p>
              <code className="text-xs bg-glass-3 text-glass-text px-1 rounded">POST /api/submissions/:id/ai-check</code> — submission caption &amp; metadata check
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Workflow className="h-5 w-5" />
            Workflow Automation & Orchestration
          </CardTitle>
          <CardDescription>
            Detect stalled items, SLA breaches, and get prioritized tasks — on Dashboard and Job Detail Workflow tab
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-glass-text-2 mb-4">
            Open <strong>Dashboard</strong> for agency-wide scan, or <strong>Jobs → any job → Workflow</strong> tab.
          </p>
          {demoJobId && (
            <AIWorkflowPanel mode="job" jobId={demoJobId} className="border-0 bg-transparent shadow-none p-0" />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <TrendingUp className="h-5 w-5" />
            Predictive Analytics & Insights
          </CardTitle>
          <CardDescription>
            AI forecasts, bottleneck detection, and portfolio recommendations — integrated in Job Detail and Analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-glass-text-2 mb-4">
            Open <strong>Jobs → any job → AI Insights tab</strong> for campaign-level forecasts, or{' '}
            <strong>Analytics</strong> for agency portfolio insights.
          </p>
          {demoJobId && (
            <AIInsightsPanel mode="job" jobId={demoJobId} className="border-0 bg-transparent shadow-none p-0" />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <DollarSign className="h-5 w-5" />
            Dynamic Pricing & Budget Optimization
          </CardTitle>
          <CardDescription>
            Market benchmarks, budget allocation, and KOL rate negotiation — integrated in Job Detail Budget tab
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-glass-text-2 mb-4">
            Open <strong>Jobs → any job → Budget</strong> tab for full pricing tools, or try the demo below.
          </p>
          {demoJobId && (
            <AIPricingPanel jobId={demoJobId} className="border-0 bg-transparent shadow-none p-0" />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <UserSearch className="h-5 w-5" />
            KOL Discovery & Recruitment
          </CardTitle>
          <CardDescription>
            Talent gap analysis, profile vetting, and recruitment outreach — on Dashboard, KOLs, and Job Detail Discovery tab
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-glass-text-2 mb-4">
            Open <strong>Dashboard</strong> or <strong>KOLs</strong> for agency-wide scout, or{' '}
            <strong>Jobs → any job → Discovery</strong> tab for campaign-specific recruitment.
          </p>
          {demoJobId && (
            <AIKOLDiscoveryPanel mode="job" jobId={demoJobId} className="border-0 bg-transparent shadow-none p-0" />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <MessageSquare className="h-5 w-5" />
            Communication Assistant — Message Generation
          </CardTitle>
          <CardDescription>
            Select a scenario, click Generate, edit the result, then accept it
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {SCENARIOS.map((s) => (
              <Badge
                key={s.id}
                variant={activeScenario === s.id ? 'default' : 'outline'}
                className="cursor-pointer px-3 py-1"
                onClick={() => setActiveScenario(s.id)}
              >
                {s.title}
              </Badge>
            ))}
          </div>

          <AIMessageGenerator
            key={scenario.id}
            endpoint={scenario.endpoint}
            payload={scenario.payload}
            onAccept={(message) => setAcceptedMessage(message)}
            title={scenario.title}
            description={scenario.description}
            buttonText={`Generate ${scenario.title}`}
          />

          {acceptedMessage && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
              <p className="text-sm font-medium text-emerald-500 dark:text-emerald-300 mb-2">Accepted message (ready to send):</p>
              <p className="text-sm whitespace-pre-wrap text-glass-text">{acceptedMessage}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Wand2 className="h-5 w-5" />
            Message Improver & Sentiment Analysis
          </CardTitle>
          <CardDescription>
            Analyze tone of draft messages and improve them with professional, friendly, diplomatic, or urgent tone
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AIMessageImprover
            initialMessage="Hey, your content is not good. Fix it ASAP."
            onImprove={(msg) => setImprovedMessage(msg)}
          />
          {improvedMessage && (
            <div className="mt-4 rounded-xl border border-accent2/30 bg-accent2/10 p-4">
              <p className="text-sm font-medium text-accent2 mb-2">Final improved message:</p>
              <p className="text-sm whitespace-pre-wrap text-glass-text">{improvedMessage}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Workflow className="h-5 w-5" />
            Workflow API Integrations
          </CardTitle>
          <CardDescription>
            Backend routes where AI is wired into existing invitation, script, and submission flows
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {WORKFLOW_INTEGRATIONS.map((item) => (
              <div
                key={item.route}
                className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl border border-glass bg-glass-2"
              >
                <code className="text-xs font-mono bg-glass-3 text-glass-text px-2 py-1 rounded border border-glass shrink-0">
                  {item.route}
                </code>
                <span className="text-sm text-glass-text-2">{item.feature}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-dashed border-emerald-500/30 bg-emerald-500/5">
        <CardHeader>
          <CardTitle className="text-lg">All 8 AI Agents Complete</CardTitle>
          <CardDescription>The full Phase 2 AI agent roadmap is now implemented</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge variant="success">Communication Assistant</Badge>
            <Badge variant="success">Smart Briefing Assistant</Badge>
            <Badge variant="success">Intelligent KOL Matching</Badge>
            <Badge variant="success">Content Quality Assurance</Badge>
            <Badge variant="success">Predictive Analytics</Badge>
            <Badge variant="success">Workflow Automation</Badge>
            <Badge variant="success">Dynamic Pricing & Budget Optimization</Badge>
            <Badge variant="success">KOL Discovery & Recruitment</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
